import React, { useState, useEffect } from 'react';
import { activityApi, auditApi, usersApi, getErrorMessage } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Activity, 
  Users, 
  FileText, 
  Download, 
  Upload,
  LogIn, 
  Clock,
  Search,
  RefreshCw,
  User,
  Filter,
  Calendar,
  ChevronDown,
  ChevronUp,
  Briefcase,
  UserPlus,
  CheckCircle,
  BarChart3,
  ListTodo
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';

const ActivityLogs = () => {
  const [activityLogs, setActivityLogs] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [userSummary, setUserSummary] = useState([]);
  const [taskSummary, setTaskSummary] = useState({ summary: [], statistics: {} });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taskSummaryLoading, setTaskSummaryLoading] = useState(false);
  const [userSummaryLoading, setUserSummaryLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('task-summary');
  const [expandedDates, setExpandedDates] = useState({});
  const [expandedRecruiters, setExpandedRecruiters] = useState({});
  
  // Date filters for task summary
  const [dateRange, setDateRange] = useState('last7days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [taskSummaryUser, setTaskSummaryUser] = useState('all');
  
  // Date filters for user summary
  const [userSummaryDateRange, setUserSummaryDateRange] = useState('all');
  const [userSummaryCustomStart, setUserSummaryCustomStart] = useState('');
  const [userSummaryCustomEnd, setUserSummaryCustomEnd] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'task-summary') {
      fetchTaskSummary();
    }
  }, [dateRange, customStartDate, customEndDate, taskSummaryUser, activeTab]);

  useEffect(() => {
    if (activeTab === 'summary') {
      fetchUserSummary();
    }
  }, [userSummaryDateRange, userSummaryCustomStart, userSummaryCustomEnd, activeTab]);

  const getDateRangeValues = (range, customStart, customEnd) => {
    const today = new Date();
    switch (range) {
      case 'today':
        return { start: format(today, 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
      case 'yesterday':
        const yesterday = subDays(today, 1);
        return { start: format(yesterday, 'yyyy-MM-dd'), end: format(yesterday, 'yyyy-MM-dd') };
      case 'last7days':
        return { start: format(subDays(today, 6), 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
      case 'last30days':
        return { start: format(subDays(today, 29), 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
      case 'thisMonth':
        return { start: format(startOfMonth(today), 'yyyy-MM-dd'), end: format(endOfMonth(today), 'yyyy-MM-dd') };
      case 'custom':
        return { start: customStart, end: customEnd };
      default:
        return { start: null, end: null };
    }
  };

  const fetchUserSummary = async () => {
    setUserSummaryLoading(true);
    try {
      const { start, end } = getDateRangeValues(userSummaryDateRange, userSummaryCustomStart, userSummaryCustomEnd);
      const res = await activityApi.getUserSummary(start, end);
      setUserSummary(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load user summary'));
    } finally {
      setUserSummaryLoading(false);
    }
  };

  const fetchTaskSummary = async () => {
    setTaskSummaryLoading(true);
    try {
      const { start, end } = getDateRangeValues(dateRange, customStartDate, customEndDate);
      const userId = taskSummaryUser === 'all' ? null : taskSummaryUser;
      const res = await activityApi.getRecruiterTaskSummary(start, end, userId);
      setTaskSummary(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load task summary'));
    } finally {
      setTaskSummaryLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [activityRes, auditRes, summaryRes, usersRes] = await Promise.all([
        activityApi.getLogs(500),
        auditApi.getLogs(500),
        activityApi.getUserSummary(),
        usersApi.getAll()
      ]);
      setActivityLogs(activityRes.data);
      setAuditLogs(auditRes.data);
      setUserSummary(summaryRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load activity logs'));
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'auth': return <LogIn className="w-4 h-4" />;
      case 'resume': return <Download className="w-4 h-4" />;
      case 'workbench': return <FileText className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'auth': return 'bg-blue-100 text-blue-700';
      case 'resume': return 'bg-green-100 text-green-700';
      case 'workbench': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-700';
      case 'delivery_manager': return 'bg-purple-100 text-purple-700';
      case 'team_lead': return 'bg-blue-100 text-blue-700';
      case 'recruiter': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getTaskIcon = (taskName) => {
    if (taskName.includes('Job')) return <Briefcase className="w-3 h-3" />;
    if (taskName.includes('Candidate') || taskName.includes('Upload')) return <UserPlus className="w-3 h-3" />;
    if (taskName.includes('Application') || taskName.includes('Interview')) return <CheckCircle className="w-3 h-3" />;
    if (taskName.includes('Match')) return <BarChart3 className="w-3 h-3" />;
    return <ListTodo className="w-3 h-3" />;
  };

  const getTaskColor = (taskName) => {
    if (taskName.includes('Created') || taskName.includes('Added')) return 'bg-green-100 text-green-700';
    if (taskName.includes('Updated') || taskName.includes('Changes')) return 'bg-blue-100 text-blue-700';
    if (taskName.includes('Approved') || taskName.includes('Published')) return 'bg-emerald-100 text-emerald-700';
    if (taskName.includes('Rejected')) return 'bg-red-100 text-red-700';
    if (taskName.includes('Match')) return 'bg-purple-100 text-purple-700';
    return 'bg-slate-100 text-slate-700';
  };

  const toggleDateExpanded = (date) => {
    setExpandedDates(prev => ({ ...prev, [date]: !prev[date] }));
  };

  const toggleRecruiterExpanded = (dateUserId) => {
    setExpandedRecruiters(prev => ({ ...prev, [dateUserId]: !prev[dateUserId] }));
  };

  const filteredActivityLogs = activityLogs.filter(log => {
    const matchesUser = selectedUser === 'all' || log.user_id === selectedUser;
    const matchesSearch = !searchTerm || 
      log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesUser && matchesSearch;
  });

  const [auditActionFilter, setAuditActionFilter] = useState('all');

  // Pre-defined quick-filter chips for high-value action families. Keeps
  // the audit page useful even without scrolling/searching.
  const AUDIT_ACTION_PRESETS = [
    { key: 'all', label: 'All actions', match: () => true },
    { key: 'application_assigned', label: 'App reassignments',
      match: (a) => a === 'application_assigned' },
    { key: 'stage_change', label: 'Stage changes',
      match: (a) => /stage/i.test(a || '') },
    { key: 'login', label: 'Logins',
      match: (a) => /login|logout|auth/i.test(a || '') },
    { key: 'client_industry_updated', label: 'Industry backfills',
      match: (a) => a === 'client_industry_updated' },
    { key: 'closure_of_week', label: 'Closure cheer',
      match: (a) => /closure_of_week|closure_cheered/i.test(a || '') },
  ];
  const matchesActionFilter = (action) => {
    const preset = AUDIT_ACTION_PRESETS.find((p) => p.key === auditActionFilter);
    return preset ? preset.match(action) : true;
  };

  const filteredAuditLogs = auditLogs.filter(log => {
    const matchesUser = selectedUser === 'all' || log.user_id === selectedUser;
    const matchesAction = matchesActionFilter(log.action);
    const matchesSearch = !searchTerm || 
      log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesUser && matchesAction && matchesSearch;
  });

  const filteredSummary = userSummary.filter(u => {
    const matchesSearch = !searchTerm || 
      u.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.user_email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Activity Logs
          </h1>
          <p className="text-slate-600 mt-1">Track user activities and system audit trail</p>
        </div>
        <Button onClick={() => { fetchData(); fetchTaskSummary(); }} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Users</p>
                <p className="text-2xl font-bold text-slate-900">{userSummary.length}</p>
              </div>
              <Users className="w-8 h-8 text-teal-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Logins</p>
                <p className="text-2xl font-bold text-slate-900">
                  {userSummary.reduce((acc, u) => acc + (u.total_logins || 0), 0)}
                </p>
              </div>
              <LogIn className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Resumes Uploaded</p>
                <p className="text-2xl font-bold text-slate-900">
                  {userSummary.reduce((acc, u) => acc + (u.resumes_uploaded || 0), 0)}
                </p>
              </div>
              <Upload className="w-8 h-8 text-teal-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Resume Downloads</p>
                <p className="text-2xl font-bold text-slate-900">
                  {userSummary.reduce((acc, u) => acc + (u.resume_downloads || 0), 0)}
                </p>
              </div>
              <Download className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Activities Today</p>
                <p className="text-2xl font-bold text-slate-900">
                  {activityLogs.filter(l => {
                    const logDate = new Date(l.timestamp).toDateString();
                    return logDate === new Date().toDateString();
                  }).length}
                </p>
              </div>
              <Activity className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search activities..."
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={selectedUser} onValueChange={setSelectedUser}>
              <SelectTrigger className="w-[200px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by user" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="task-summary" className="flex items-center gap-1">
            <BarChart3 className="w-4 h-4" />
            Task Summary
          </TabsTrigger>
          <TabsTrigger value="summary">User Summary</TabsTrigger>
          <TabsTrigger value="activity">Activity Log</TabsTrigger>
          <TabsTrigger value="audit">Audit Trail</TabsTrigger>
        </TabsList>

        {/* Task Summary Tab - NEW */}
        <TabsContent value="task-summary">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-teal-600" />
                    Date-wise Recruiter Task Summary
                  </CardTitle>
                  <CardDescription>View tasks completed by each recruiter grouped by date</CardDescription>
                </div>
                
                {/* Date Range Filters */}
                <div className="flex items-center gap-3">
                  <Select value={dateRange} onValueChange={setDateRange}>
                    <SelectTrigger className="w-[150px]">
                      <Calendar className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Date range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="yesterday">Yesterday</SelectItem>
                      <SelectItem value="last7days">Last 7 Days</SelectItem>
                      <SelectItem value="last30days">Last 30 Days</SelectItem>
                      <SelectItem value="thisMonth">This Month</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                      <SelectItem value="all">All Time</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {dateRange === 'custom' && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="w-[140px]"
                      />
                      <span className="text-slate-400">to</span>
                      <Input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="w-[140px]"
                      />
                    </div>
                  )}
                  
                  <Select value={taskSummaryUser} onValueChange={setTaskSummaryUser}>
                    <SelectTrigger className="w-[180px]">
                      <User className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Filter recruiter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Recruiters</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Statistics Overview */}
              {taskSummary.statistics && (
                <div className="grid grid-cols-4 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-teal-700">{taskSummary.statistics.total_tasks || 0}</p>
                    <p className="text-sm text-slate-500">Total Tasks</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-700">{taskSummary.statistics.unique_users || 0}</p>
                    <p className="text-sm text-slate-500">Active Users</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-700">{taskSummary.statistics.total_dates || 0}</p>
                    <p className="text-sm text-slate-500">Days</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-700">
                      {taskSummary.statistics.total_dates > 0 
                        ? Math.round(taskSummary.statistics.total_tasks / taskSummary.statistics.total_dates) 
                        : 0}
                    </p>
                    <p className="text-sm text-slate-500">Avg Tasks/Day</p>
                  </div>
                </div>
              )}
              
              {taskSummaryLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
                </div>
              ) : taskSummary.summary?.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No tasks found for the selected period</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {taskSummary.summary?.map((dayData) => (
                    <div key={dayData.date} className="border rounded-lg overflow-hidden">
                      {/* Date Header */}
                      <div 
                        className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => toggleDateExpanded(dayData.date)}
                        data-testid={`date-header-${dayData.date}`}
                      >
                        <div className="flex items-center gap-3">
                          <Calendar className="w-5 h-5 text-teal-600" />
                          <div>
                            <p className="font-semibold text-slate-900">
                              {format(new Date(dayData.date), 'EEEE, MMMM d, yyyy')}
                            </p>
                            <p className="text-sm text-slate-500">
                              {dayData.active_users} active user{dayData.active_users !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <Badge className="bg-teal-100 text-teal-700 text-lg px-3 py-1">
                            {dayData.total_tasks} tasks
                          </Badge>
                          {expandedDates[dayData.date] ? (
                            <ChevronUp className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                      </div>
                      
                      {/* Recruiters List */}
                      {expandedDates[dayData.date] && (
                        <div className="divide-y">
                          {dayData.recruiters.map((recruiter) => {
                            const recruiterId = `${dayData.date}-${recruiter.user_id}`;
                            return (
                              <div key={recruiterId} className="bg-white">
                                {/* Recruiter Header */}
                                <div 
                                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                                  onClick={() => toggleRecruiterExpanded(recruiterId)}
                                  data-testid={`recruiter-row-${recruiterId}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                                      <User className="w-5 h-5 text-teal-700" />
                                    </div>
                                    <div>
                                      <p className="font-medium text-slate-900">{recruiter.user_name}</p>
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm text-slate-500">{recruiter.user_email}</span>
                                        <Badge className={`text-xs ${getRoleColor(recruiter.user_role)}`}>
                                          {recruiter.user_role?.replace('_', ' ')}
                                        </Badge>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <div className="flex flex-wrap gap-2">
                                      {Object.entries(recruiter.tasks_breakdown || {}).slice(0, 4).map(([task, count]) => (
                                        <Badge 
                                          key={task} 
                                          variant="outline" 
                                          className={`flex items-center gap-1 ${getTaskColor(task)}`}
                                        >
                                          {getTaskIcon(task)}
                                          {task}: {count}
                                        </Badge>
                                      ))}
                                      {Object.keys(recruiter.tasks_breakdown || {}).length > 4 && (
                                        <Badge variant="outline" className="bg-slate-100">
                                          +{Object.keys(recruiter.tasks_breakdown).length - 4} more
                                        </Badge>
                                      )}
                                    </div>
                                    <Badge className="bg-blue-100 text-blue-700 font-bold">
                                      {recruiter.total_tasks} total
                                    </Badge>
                                    {expandedRecruiters[recruiterId] ? (
                                      <ChevronUp className="w-4 h-4 text-slate-400" />
                                    ) : (
                                      <ChevronDown className="w-4 h-4 text-slate-400" />
                                    )}
                                  </div>
                                </div>
                                
                                {/* Expanded Task Details */}
                                {expandedRecruiters[recruiterId] && (
                                  <div className="px-4 pb-4 bg-slate-50">
                                    <div className="grid grid-cols-2 gap-4">
                                      {/* Task Breakdown */}
                                      <div className="bg-white p-4 rounded-lg border">
                                        <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                                          <BarChart3 className="w-4 h-4" />
                                          Task Breakdown
                                        </h4>
                                        <div className="space-y-2">
                                          {Object.entries(recruiter.tasks_breakdown || {}).map(([task, count]) => (
                                            <div key={task} className="flex items-center justify-between">
                                              <span className="text-sm text-slate-600 flex items-center gap-2">
                                                {getTaskIcon(task)}
                                                {task}
                                              </span>
                                              <span className="font-medium text-slate-900">{count}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                      
                                      {/* Recent Activities */}
                                      <div className="bg-white p-4 rounded-lg border">
                                        <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                                          <Clock className="w-4 h-4" />
                                          Recent Activities
                                        </h4>
                                        <div className="space-y-2">
                                          {recruiter.recent_activities?.map((activity, idx) => (
                                            <div key={idx} className="text-sm border-l-2 border-teal-200 pl-3">
                                              <p className="font-medium text-slate-700">{activity.action}</p>
                                              <p className="text-slate-500 truncate">{activity.details}</p>
                                              <p className="text-xs text-slate-400">
                                                {format(new Date(activity.timestamp), 'HH:mm:ss')}
                                              </p>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Summary Tab */}
        <TabsContent value="summary">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-teal-600" />
                    User Activity Summary
                  </CardTitle>
                  <CardDescription>Overview of all user activities including resume uploads and downloads</CardDescription>
                </div>
                
                {/* Date Range Filters */}
                <div className="flex items-center gap-3">
                  <Select value={userSummaryDateRange} onValueChange={setUserSummaryDateRange}>
                    <SelectTrigger className="w-[150px]">
                      <Calendar className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Date range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="yesterday">Yesterday</SelectItem>
                      <SelectItem value="last7days">Last 7 Days</SelectItem>
                      <SelectItem value="last30days">Last 30 Days</SelectItem>
                      <SelectItem value="thisMonth">This Month</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                      <SelectItem value="all">All Time</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {userSummaryDateRange === 'custom' && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="date"
                        value={userSummaryCustomStart}
                        onChange={(e) => setUserSummaryCustomStart(e.target.value)}
                        className="w-[140px]"
                      />
                      <span className="text-slate-400">to</span>
                      <Input
                        type="date"
                        value={userSummaryCustomEnd}
                        onChange={(e) => setUserSummaryCustomEnd(e.target.value)}
                        className="w-[140px]"
                      />
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {userSummaryLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
                </div>
              ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-center">Logins</TableHead>
                    <TableHead className="text-center">
                      <div className="flex flex-col items-center">
                        <span>Resume</span>
                        <span className="text-xs text-teal-600">Uploads</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center">
                      <div className="flex flex-col items-center">
                        <span>Bulk</span>
                        <span className="text-xs text-purple-600">Uploads</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center">
                      <div className="flex flex-col items-center">
                        <span>Resume</span>
                        <span className="text-xs text-green-600">Downloads</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center">Candidates</TableHead>
                    <TableHead className="text-center">Applications</TableHead>
                    <TableHead>Last Activity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSummary.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-slate-500 py-8">
                        No activity found for the selected date range
                      </TableCell>
                    </TableRow>
                  ) : (
                  filteredSummary.map((user) => (
                    <TableRow key={user.user_id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-teal-700" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{user.user_name}</p>
                            <p className="text-sm text-slate-500">{user.user_email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getRoleColor(user.user_role)}>
                          {user.user_role?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center font-medium">{user.total_logins || 0}</TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium text-teal-600">{user.resumes_uploaded || 0}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium text-purple-600">{user.bulk_uploads || 0}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium text-green-600">{user.resume_downloads || 0}</span>
                      </TableCell>
                      <TableCell className="text-center">{user.candidates_created || 0}</TableCell>
                      <TableCell className="text-center">{user.applications_created || 0}</TableCell>
                      <TableCell>
                        {user.last_activity ? (
                          <span className="text-sm text-slate-500">
                            {format(new Date(user.last_activity), 'MMM dd, yyyy HH:mm')}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400">Never</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                  )}
                </TableBody>
              </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Log Tab */}
        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle>Activity Log</CardTitle>
              <CardDescription>Real-time user activity tracking</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {filteredActivityLogs.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No activity logs found</p>
                ) : (
                  filteredActivityLogs.map((log) => (
                    <div key={log.id} className="flex items-start gap-4 p-3 bg-slate-50 rounded-lg">
                      <div className={`p-2 rounded-full ${getCategoryColor(log.category)}`}>
                        {getCategoryIcon(log.category)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{log.user_name}</span>
                          <Badge variant="outline" className="text-xs">{log.action}</Badge>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">{log.details}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                          <Clock className="w-3 h-3" />
                          {format(new Date(log.timestamp), 'MMM dd, yyyy HH:mm:ss')}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Trail Tab */}
        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle>Audit Trail</CardTitle>
              <CardDescription>Complete audit trail of all system actions</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Quick-filter chips for high-signal action families.
                  Stacked on top of the existing search box so users can
                  scope first, then keyword-search inside that scope. */}
              <div className="flex flex-wrap gap-1.5 mb-3" data-testid="audit-action-chips">
                {AUDIT_ACTION_PRESETS.map((p) => {
                  const active = auditActionFilter === p.key;
                  // Live count so the chips also work as discovery affordances.
                  const count = auditLogs.filter((l) => p.match(l.action)).length;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setAuditActionFilter(p.key)}
                      className={`text-[11px] rounded-full px-2.5 py-1 border transition ${
                        active
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                      data-testid={`audit-action-chip-${p.key}`}
                    >
                      {p.label}
                      <span className={`ml-1.5 ${active ? 'text-slate-300' : 'text-slate-400'}`}>{count}</span>
                    </button>
                  );
                })}
              </div>
              {/* Audit table grows with content; pass `containerClassName` so
                  the Table wrapper becomes the scroll ancestor and the sticky
                  <thead> below actually stays pinned. */}
              <Table containerClassName="max-h-[calc(100vh-340px)] min-h-[320px]" data-testid="activity-audit-scroll">
                <TableHeader className="sticky top-0 z-10 bg-white shadow-sm">
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAuditLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                        No audit logs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAuditLogs.slice(0, 100).map((log) => (
                      <TableRow key={log.id} data-testid={`audit-row-${log.action}`}>
                        <TableCell className="text-sm text-slate-500">
                          {format(new Date(log.timestamp), 'MMM dd, HH:mm:ss')}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">{log.user_name || 'Unknown'}</p>
                            <p className="text-xs text-slate-500">{log.user_email || ''}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{log.action}</Badge>
                        </TableCell>
                        <TableCell className="max-w-md text-sm text-slate-600">
                          {/* Richer rendering for the strict-RBAC hand-off:
                              admins need to see at a glance "who handed off
                              candidate X (from A) to B on job J" — the
                              lineage. We pull from `metadata` which the
                              backend now populates with full context. */}
                          {log.action === 'application_assigned' && log.metadata ? (
                            <div className="space-y-1" data-testid="audit-row-assigned-detail">
                              <div className="font-medium text-slate-800 truncate">
                                {log.metadata.candidate_name || 'Candidate'}
                                {log.metadata.job_title ? (
                                  <span className="text-slate-500 font-normal"> · {log.metadata.job_title}</span>
                                ) : null}
                              </div>
                              <div className="flex items-center gap-1.5 flex-wrap text-xs">
                                <Badge variant="outline" className="bg-slate-50 text-slate-600">
                                  {log.metadata.previous_name || 'Unassigned'}
                                </Badge>
                                <span className="text-slate-400">→</span>
                                <Badge
                                  variant="outline"
                                  className={log.metadata.assigned_to_name
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : 'bg-amber-50 text-amber-700 border-amber-200'}
                                >
                                  {log.metadata.assigned_to_name || 'Cleared'}
                                </Badge>
                              </div>
                            </div>
                          ) : (
                            <span className="truncate block">{log.details}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ActivityLogs;
